import clsx from "clsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  Modal,
  Slide,
  Slider,
  useSlider,
} from "@transfr-inc/dashboard-components";

import { useOrganizationTags } from "@hooks/queries";
import { TagType } from "@models";

import { DeleteTagSlide, TagFormSlide, TagsListSlide } from "./components";

import "./manage-device-tags.modal.scss";

export const Slides = {
  LIST: 0,
  FORM: 1,
  DELETE: 2,
};

const TOTAL_SLIDES = Object.keys(Slides).length;

export const ManageDeviceTagsModal = ({
  open = false,
  onClose,
  organizationCode,
  initialSlide = Slides.LIST,
}) => {
  const slider = useSlider({
    totalSlides: TOTAL_SLIDES,
    initialIndex: initialSlide,
  });
  const [selectedTag, setSelectedTag] = useState();
  const [activeSlideHeight, setActiveSlideHeight] = useState();
  // Animate the active-slide height only after navigating; initial open is
  // measured before first paint, so it sizes instantly.
  const [animated, setAnimated] = useState(false);
  const slideRefs = useRef([]);
  const heightRef = useRef();

  const { data: tags, isFetching: isFetchingDeviceTags } = useOrganizationTags(
    organizationCode,
    TagType.DEVICE,
    { enabled: open && !!organizationCode }
  );

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        slider.goSlide(initialSlide);
        setSelectedTag();
        setAnimated(false);
      }, 1000);
    }
  }, [open]);

  const goSlide = (index) => {
    setAnimated(true);
    slider.goSlide(index);
  };

  // Size the viewport to the active slide: grow immediately so the incoming
  // slide isn't clipped, defer shrinking until the outgoing one is gone.
  useLayoutEffect(() => {
    if (!open) return;

    const element = slideRefs.current[slider.currentIndex];
    if (!element) return;

    let shrinkTimer;
    const applyHeight = (height) => {
      heightRef.current = height;
      setActiveSlideHeight(height);
    };

    const updateHeight = () => {
      const target = element.clientHeight;
      clearTimeout(shrinkTimer);
      if (heightRef.current == null || target >= heightRef.current) {
        applyHeight(target);
      } else {
        shrinkTimer = setTimeout(() => applyHeight(target), 150);
      }
    };
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => {
      observer.disconnect();
      clearTimeout(shrinkTimer);
    };
  }, [open, slider.currentIndex]);

  const handleCreateClick = () => {
    setSelectedTag();
    goSlide(Slides.FORM);
  };

  const handleEditClick = (tag) => {
    setSelectedTag(tag);
    goSlide(Slides.FORM);
  };

  const handleDeleteClick = (tag) => {
    setSelectedTag(tag);
    goSlide(Slides.DELETE);
  };

  const handleBackToList = () => {
    goSlide(Slides.LIST);
    setSelectedTag();
  };

  // Opened straight into the form: exit returns closes the modal.
  const handleFormExit =
    initialSlide === Slides.FORM ? onClose : handleBackToList;

  return (
    <Modal
      modalClassName="manage-device-tags-modal"
      open={open}
      onClose={onClose}
    >
      <div
        className={clsx("tags-slider", animated && "animated")}
        style={
          activeSlideHeight
            ? { "--active-slide-height": `${activeSlideHeight}px` }
            : undefined
        }
      >
        <Slider
          totalSlides={TOTAL_SLIDES}
          currentSlideIndex={slider.currentIndex}
        >
          <Slide>
            <div ref={(el) => (slideRefs.current[Slides.LIST] = el)}>
              <TagsListSlide
                tags={tags}
                isFetching={isFetchingDeviceTags}
                onCreateClick={handleCreateClick}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
                onClose={onClose}
              />
            </div>
          </Slide>
          <Slide>
            <div ref={(el) => (slideRefs.current[Slides.FORM] = el)}>
              <TagFormSlide
                organizationCode={organizationCode}
                tag={selectedTag}
                onBack={handleFormExit}
                onDone={handleFormExit}
              />
            </div>
          </Slide>
          <Slide>
            <div ref={(el) => (slideRefs.current[Slides.DELETE] = el)}>
              <DeleteTagSlide
                organizationCode={organizationCode}
                tag={selectedTag}
                onBack={handleBackToList}
                onDone={handleBackToList}
              />
            </div>
          </Slide>
        </Slider>
      </div>
    </Modal>
  );
};
